import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ZoomOut, ZoomIn } from 'lucide-react';
import { loadImageFromUrl, encodeSquareCanvas } from '../utils/imageResize';
const VIEWPORT = 256; // px — the square crop window; its inscribed circle is the avatar
const OUTPUT = 256; // px — exported image size
const MAX_ZOOM = 3;
// Interactive avatar cropper: drag to reposition, zoom to scale, with a circular
// mask that previews exactly what the round avatar will show. Exports the framed
// square as JPEG bytes for upload.
export const AvatarCropper = ({ file, onComplete, onCancel }) => {
    const [img, setImg] = useState(null);
    const [imgUrl, setImgUrl] = useState('');
    const [baseScale, setBaseScale] = useState(1); // scale that makes the image cover the viewport at zoom 1
    const [zoom, setZoom] = useState(1); // >= 1
    const [offset, setOffset] = useState({ x: 0, y: 0 }); // px from centered
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const dragRef = useRef(null);
    const viewportRef = useRef(null);
    // Load the chosen file; keep the object URL alive for display until unmount.
    useEffect(() => {
        const url = URL.createObjectURL(file);
        setImgUrl(url);
        let cancelled = false;
        loadImageFromUrl(url)
            .then((image) => {
            if (cancelled)
                return;
            setImg(image);
            setBaseScale(VIEWPORT / Math.min(image.width, image.height));
            setZoom(1);
            setOffset({ x: 0, y: 0 });
        })
            .catch(() => !cancelled && setError('Could not read that image.'));
        return () => {
            cancelled = true;
            URL.revokeObjectURL(url);
        };
    }, [file]);
    const displayScale = baseScale * zoom;
    const dispW = img ? img.width * displayScale : 0;
    const dispH = img ? img.height * displayScale : 0;
    // Keep the image covering the viewport — never reveal empty edges.
    const clamp = useCallback((o) => {
        const maxX = Math.max(0, (dispW - VIEWPORT) / 2);
        const maxY = Math.max(0, (dispH - VIEWPORT) / 2);
        return {
            x: Math.max(-maxX, Math.min(maxX, o.x)),
            y: Math.max(-maxY, Math.min(maxY, o.y)),
        };
    }, [dispW, dispH]);
    useEffect(() => {
        setOffset((o) => clamp(o));
    }, [zoom, clamp]);
    // Non-passive wheel listener so we can preventDefault and zoom over the image.
    useEffect(() => {
        const el = viewportRef.current;
        if (!el)
            return;
        const onWheel = (e) => {
            e.preventDefault();
            setZoom((z) => Math.min(MAX_ZOOM, Math.max(1, z - e.deltaY * 0.0015)));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [img]);
    const onPointerDown = (e) => {
        dragRef.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!dragRef.current)
            return;
        const dx = e.clientX - dragRef.current.x;
        const dy = e.clientY - dragRef.current.y;
        dragRef.current = { x: e.clientX, y: e.clientY };
        setOffset((o) => clamp({ x: o.x + dx, y: o.y + dy }));
    };
    const onPointerUp = (e) => {
        var _a, _b;
        dragRef.current = null;
        (_b = (_a = e.currentTarget).releasePointerCapture) === null || _b === void 0 ? void 0 : _b.call(_a, e.pointerId);
    };
    const handleUse = async () => {
        if (!img || busy)
            return;
        setBusy(true);
        setError('');
        try {
            const canvas = document.createElement('canvas');
            canvas.width = OUTPUT;
            canvas.height = OUTPUT;
            const ctx = canvas.getContext('2d');
            if (!ctx)
                throw new Error('Image processing is unavailable in this browser.');
            // Map the viewport window back into the source image's natural pixels.
            const imgLeft = (VIEWPORT - dispW) / 2 + offset.x;
            const imgTop = (VIEWPORT - dispH) / 2 + offset.y;
            const sx = -imgLeft / displayScale;
            const sy = -imgTop / displayScale;
            const sSize = VIEWPORT / displayScale;
            ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
            const prepared = await encodeSquareCanvas(canvas);
            onComplete(prepared);
        }
        catch (e) {
            setError((e === null || e === void 0 ? void 0 : e.message) || 'Could not prepare that image.');
            setBusy(false);
        }
    };
    return (_jsxs("div", { className: "fixed inset-0 z-[80] flex items-center justify-center p-4", children: [_jsx("div", { className: "ls-modal-backdrop", onClick: onCancel }), _jsxs("div", { className: "ls-modal-card ls-modal-card-animate relative w-full max-w-sm p-6 flex flex-col items-center", style: { maxHeight: '92vh', backgroundColor: 'var(--sand)' }, children: [_jsx("h3", { className: "text-lg font-semibold mb-1 self-start", style: { color: 'var(--charcoal)' }, children: "Position your photo" }), _jsx("p", { className: "text-xs mb-4 self-start", style: { color: 'var(--forest)' }, children: "Drag to reposition, and zoom to frame. The circle is exactly what others will see." }), _jsxs("div", { ref: viewportRef, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, onPointerCancel: onPointerUp, className: "relative overflow-hidden select-none touch-none", style: {
                            width: VIEWPORT,
                            height: VIEWPORT,
                            borderRadius: 8,
                            cursor: dragRef.current ? 'grabbing' : 'grab',
                            backgroundColor: 'var(--charcoal)',
                        }, children: [img && imgUrl && (_jsx("img", { src: imgUrl, alt: "", draggable: false, style: {
                                    position: 'absolute',
                                    left: (VIEWPORT - dispW) / 2 + offset.x,
                                    top: (VIEWPORT - dispH) / 2 + offset.y,
                                    width: dispW,
                                    height: dispH,
                                    maxWidth: 'none',
                                    pointerEvents: 'none',
                                } })), _jsx("div", { className: "absolute inset-0 pointer-events-none", style: {
                                    borderRadius: '50%',
                                    boxShadow: '0 0 0 9999px rgba(20, 28, 22, 0.55), inset 0 0 0 2px rgba(255,255,255,0.85)',
                                } })] }), _jsxs("div", { className: "flex items-center gap-3 w-full mt-4", style: { maxWidth: VIEWPORT }, children: [_jsx(ZoomOut, { size: 16, style: { color: 'var(--forest)' } }), _jsx("input", { type: "range", min: 1, max: MAX_ZOOM, step: 0.01, value: zoom, onChange: (e) => setZoom(parseFloat(e.target.value)), className: "flex-1", style: { accentColor: 'var(--forest)' }, "aria-label": "Zoom" }), _jsx(ZoomIn, { size: 16, style: { color: 'var(--forest)' } })] }), error && (_jsx("p", { className: "text-xs mt-3 self-start", style: { color: '#dc2626' }, children: error })), _jsxs("div", { className: "flex items-center justify-end gap-2 w-full mt-5", children: [_jsx("button", { type: "button", onClick: onCancel, disabled: busy, className: "px-3.5 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-60", style: { backgroundColor: 'transparent', color: 'var(--charcoal)', borderColor: 'var(--pewter)' }, children: "Cancel" }), _jsx("button", { type: "button", onClick: handleUse, disabled: !img || busy, className: "px-5 py-2 text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: busy ? 'Preparing…' : 'Use photo' })] })] })] }));
};
